// react
import { Suspense, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import SeuDatagrid from '@/features/energyxplorer/components/esg/SeuDatagrid';
import WaterHeatDeclarationCard from '@/features/energyxplorer/components/esg/WaterHeatDeclarationCard';
import YearOnYearGraph from '@/features/energyxplorer/components/esg/YearOnYearGraph';

// feature-hooks
import { useEsgUrlDateRange } from '@/features/energyxplorer/hooks/useEsgUrlDateRange';

// feature-utils
import { getAnalysisTypeIcon } from '@/features/energyxplorer/utils/analysis-type-helpers';

// relative
import {
  useGetCO2eEmissionDataHeatWaterQuery,
  useGetCO2eEmissionDataPerNodeHeatWaterQuery,
  useGetCO2eEmissionTimestampSummaryHeatWaterQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import { showSnackbar } from '../../../../redux/snackbarSlice';

import { DateTime } from 'luxon';

const ESG_ZONE = 'Europe/Copenhagen';

const ESGWaterHeatPage = () => {
  const { analysisType, accountId, energyGroupId } = useParams({
    strict: false,
  });

  const { t } = useTranslation('energyxplorer-esg');
  const theme = useTheme();
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );

  // ESG-specific date range with its own query params (esgFrom/esgTo)
  const { from, to, timeRange, setRange } = useEsgUrlDateRange({
    zone: ESG_ZONE,
  });

  const dispatch = useDispatch();

  const {
    data: timestampsSummary,
    isSuccess: isTimestampsSummarySuccess,
    isFetching: isTimestampsSummaryFetching,
    isError: isTimestampsSummaryError,
  } = useGetCO2eEmissionTimestampSummaryHeatWaterQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
    },
    {
      skip: !accountId || !selectedEnergyGroup,
    },
  );
  const dataWindow = useMemo(() => {
    if (!isTimestampsSummarySuccess || !timestampsSummary) return null;
    if (!from || !to) return null;

    const zone = from.zoneName || ESG_ZONE;
    const { maxEarliestDataTimestamp, minLatestDataTimestamp } =
      timestampsSummary || {};
    if (!maxEarliestDataTimestamp || !minLatestDataTimestamp) return null;

    const maxEarliest = DateTime.fromISO(maxEarliestDataTimestamp).setZone(
      zone,
    );
    const minLatest = DateTime.fromISO(minLatestDataTimestamp).setZone(zone);

    if (!maxEarliest.isValid || !minLatest.isValid) return null;

    return { maxEarliest, minLatest };
  }, [isTimestampsSummarySuccess, timestampsSummary, from, to]);

  const isTimestampValid = useMemo(() => {
    if (!dataWindow) return false;
    return from >= dataWindow.maxEarliest && to <= dataWindow.minLatest;
  }, [dataWindow, from, to]);

  const {
    data: esgOverviewData,
    isError: isEsgOverviewDataError,
    isFetching: isEsgOverviewDataFetching,
    isSuccess: isEsgOverviewDataSuccess,
  } = useGetCO2eEmissionDataHeatWaterQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      start: timeRange?.start,
      end: timeRange?.end,
    },
    {
      skip:
        !accountId ||
        selectedEnergyGroup === null ||
        !timeRange ||
        timeRange.start == null ||
        timeRange.end == null ||
        !isTimestampValid,
    },
  );
  const co2eEmissionsSummary = useMemo(
    () => esgOverviewData?.CO2eEmissionsSummary,
    [esgOverviewData],
  );

  const seuData = useMemo(
    () => esgOverviewData?.MonthlyCO2eEmissions || [],
    [esgOverviewData],
  );
  const isPortfolioEnergyGroup =
    selectedEnergyGroup?.type.includes('Portfolio');
  const { data: esgNodeData, isFetching: isEsgNodeDataFetching } =
    useGetCO2eEmissionDataPerNodeHeatWaterQuery(
      {
        accountId: accountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        start: timeRange?.start,
        end: timeRange?.end,
      },
      {
        skip:
          !accountId ||
          selectedEnergyGroup === null ||
          !isPortfolioEnergyGroup ||
          !timeRange ||
          timeRange.start == null ||
          timeRange.end == null ||
          !isTimestampValid,
      },
    );
  const esgNodes = esgNodeData?.Nodes || [];
  // Clamp ESG URL date range to available data window from timestampsSummary
  useEffect(() => {
    if (!isTimestampsSummarySuccess || !timestampsSummary) return;
    if (!from || !to) return;

    const zone = from.zoneName || ESG_ZONE;

    const { maxEarliestDataTimestamp, minLatestDataTimestamp } =
      timestampsSummary || {};

    if (!maxEarliestDataTimestamp || !minLatestDataTimestamp) return;

    const maxEarliest = DateTime.fromISO(maxEarliestDataTimestamp).setZone(
      zone,
    );
    const minLatest = DateTime.fromISO(minLatestDataTimestamp).setZone(zone);

    if (!maxEarliest.isValid || !minLatest.isValid) return;

    const currentStart = from;
    const currentEnd = to;

    const isCompletelyOutside =
      currentEnd < maxEarliest || currentStart > minLatest;

    let adjustedStart;
    let adjustedEnd;

    if (isCompletelyOutside) {
      // Full fallback: select full available window
      adjustedStart = maxEarliest;
      adjustedEnd = minLatest;
    } else {
      // Normal adjustment: clip only start or end
      adjustedStart = currentStart < maxEarliest ? maxEarliest : currentStart;
      adjustedEnd = currentEnd > minLatest ? minLatest : currentEnd;

      // Extra safety: if adjustment somehow causes inverted range
      if (adjustedStart > adjustedEnd) {
        adjustedStart = maxEarliest;
        adjustedEnd = minLatest;
      }
    }

    const noChange =
      adjustedStart.toISO() === currentStart.toISO() &&
      adjustedEnd.toISO() === currentEnd.toISO();

    if (noChange) return;

    // Avoid extra clamping: we've already chosen the range, so disable clampToNow and rounding
    setRange(adjustedStart, adjustedEnd, {
      roundToDay: false,
      clampToNow: false,
    });

    dispatch(
      showSnackbar({
        severity: 'info',
        message: t('snackbar.timerange_changed'),
      }),
    );
  }, [
    isTimestampsSummarySuccess,
    timestampsSummary,
    from,
    to,
    setRange,
    dispatch,
    t,
    dataWindow,
  ]);

  if (
    isEsgOverviewDataFetching ||
    isTimestampsSummaryFetching ||
    isEsgNodeDataFetching ||
    !timeRange
  ) {
    return (
      <Box height={'60vh'}>
        <GatewayLoadingIndicator
          isLoading={
            isEsgOverviewDataFetching ||
            isEsgNodeDataFetching ||
            isTimestampsSummaryFetching ||
            !timeRange
          }
          loadingText={t('loading_page_text')}
          disableBackdrop={true}
        />
      </Box>
    );
  }

  if (isEsgOverviewDataError || isTimestampsSummaryError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Card
          width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}
          sx={{ p: 2, mb: 2, mt: 8, borderRadius: 2 }}
        >
          <CardContent width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}>
            <Typography variant="h6">{t('error_card.title')}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t('error_card.description')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <PageContainer maxWidth="xl" sx={{ py: 3 }}>
      {isEsgOverviewDataSuccess && esgOverviewData && (
        <Box>
          <Grid container spacing={2} paddingTop={0}>
            {/* Declaration Card */}
            {co2eEmissionsSummary && (
              <Grid
                size={{
                  xs: 12,
                  md: 12,
                  lg: 12
                }}>
                <Suspense
                  fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
                >
                  <WaterHeatDeclarationCard
                    icon={getAnalysisTypeIcon(analysisType)}
                    iconColor={theme.palette[analysisType].main}
                    energySummary={co2eEmissionsSummary}
                    title={
                      selectedEnergyGroup?.type === 'PortfolioWater'
                        ? t('water_heat_declaration.water_title', 'Vand')
                        : t('water_heat_declaration.heat_title', 'Varme')
                    }
                    tooltip={
                      selectedEnergyGroup?.type === 'PortfolioWater'
                        ? t(
                            'water_heat_declaration.water_tooltip',
                            'CO2e emissions from water consumption',
                          )
                        : t(
                            'water_heat_declaration.heat_tooltip',
                            'CO2e emissions from heat consumption',
                          )
                    }
                  />
                </Suspense>
              </Grid>
            )}

            {/* Monthly Data Grid */}
            {seuData.length > 0 && (
              <>
                {isPortfolioEnergyGroup &&
                Array.isArray(esgNodes) &&
                esgNodes.length > 0 ? (
                  <Grid
                    sx={{ transition: 'flex-basis 0.3s ease' }}
                    size={{
                      md: 12
                    }}>
                    <Suspense
                      fallback={
                        <div>{t('actions.loading', { ns: 'common' })}</div>
                      }
                    >
                      <SeuDatagrid data={[...seuData, ...esgNodes]} />
                    </Suspense>
                  </Grid>
                ) : (
                  <Grid
                    sx={{ transition: 'flex-basis 0.3s ease' }}
                    size={{
                      md: 12
                    }}>
                    <Suspense
                      fallback={
                        <div>{t('actions.loading', { ns: 'common' })}</div>
                      }
                    >
                      <SeuDatagrid data={seuData} />
                    </Suspense>
                  </Grid>
                )}
                {/* Year on Year Graph */}
                <Grid size={12}>
                  <Suspense
                    fallback={
                      <div>{t('actions.loading', { ns: 'common' })}</div>
                    }
                  >
                    <YearOnYearGraph data={seuData} />
                  </Suspense>
                </Grid>
              </>
            )}

            {/* ESG Initiatives */}
            {/*             <Grid item xs={12}>
              <Suspense fallback={<div>Loading...</div>}>
                <EsgInitiativesDatagrid
                  selectedEnergyGroup={selectedEnergyGroup}
                  timerangeMS={timeRange}
                />
              </Suspense>
            </Grid> */}
          </Grid>
        </Box>
      )}
    </PageContainer>
  );
};

export default ESGWaterHeatPage;
